<script>
    import { browser } from '$app/environment';
    import { page } from '$app/stores';
    import Button from '$lib/atoms/Button.svelte';
    import { getConfig } from '$lib/config/environment.js';
    import { t } from '$lib/i18n';
    import { loadGooglePlaces } from '$lib/services/google-maps-loader.js';
    import { Calendar, Search } from 'lucide-svelte';
    import { createEventDispatcher, onMount } from 'svelte';

    const dispatch = createEventDispatcher();
    const config = getConfig();

    const lang = $page.params.lang || 'en';

    // Props
    export let redirectUrl = `/${lang}/search`;
    export let instanceId = Math.random().toString(36).substr(2, 9); // ID unico per istanza
    export let unstyled = false; // Remove box styling when true
    
    // External data props
    export let initialAddress = '';
    export let initialLat = null;
    export let initialLng = null;
    export let initialPid = null;
    export let initialStartDate = '';
    export let initialStartTime = '';
    export let initialEndDate = '';
    export let initialEndTime = '';

    // State
    let address = '';
    let startDate = '';
    let startTime = '';
    let endDate = '';
    let endTime = '';
    let autocompleteService;
    let placesService;
    // Lega Autocomplete e la Place Details finale sotto la stessa "sessione":
    // senza questo token Google fattura ogni richiesta di Autocomplete a
    // tariffa piena invece che come sessione unica con la Place Details.
    let sessionToken = null;
    let addressInput;
    let showSuggestions = false;
    let suggestions = [];
    let selectedSuggestionIndex = -1;
    
    // Current location state (for using initial coordinates)
    let currentLat = null;
    let currentLng = null;
    let currentPid = null;
    let userIsTyping = false;
    let userHasSelectedSuggestion = false;
    
    // Modal state
    let showModal = false;
    let modalType = ''; // 'start' or 'end'
    let tempDate = '';
    let tempTime = '';
    let currentMonth = new Date().getMonth();
    let currentYear = new Date().getFullYear();

    onMount(async () => {
        if (browser) {
            await loadGooglePlaces();
            
            // Delay per assicurarsi che le Google Maps API siano completamente caricate
            setTimeout(() => {
                initializeAutocomplete();
                
                // Initialize values from props
                if (initialAddress && initialAddress !== 'Current location') {
                    address = initialAddress;
                }
                if (initialLat && initialLng) {
                    currentLat = parseFloat(initialLat);
                    currentLng = parseFloat(initialLng);
                }
                if (initialPid) {
                    currentPid = initialPid;
                }
                if (initialStartDate) {
                    startDate = initialStartDate;
                }
                if (initialStartTime) {
                    startTime = initialStartTime;
                }
                if (initialEndDate) {
                    endDate = initialEndDate;
                }
                if (initialEndTime) {
                    endTime = initialEndTime;
                }
                
                // Perform reverse geocoding if coordinates are provided but address is generic
                if (initialLat && initialLng && (!initialAddress || initialAddress === 'Current location')) {
                    reverseGeocode(initialLat, initialLng);
                }
            }, 500);
            
            // Click fuori per chiudere suggerimenti - specifico per questa istanza
            const clickHandler = (e) => {
                const searchBarElement = e.target.closest(`[data-searchbar-id="${instanceId}"]`);
                const suggestionElement = e.target.closest('.suggestions-dropdown');
                
                if (!searchBarElement && !suggestionElement) {
                    hideSuggestions();
                }
            };
            
            document.addEventListener('click', clickHandler);
            
            // Cleanup dell'event listener
            return () => {
                document.removeEventListener('click', clickHandler);
            };
        }
    });

    // Reverse geocoding function
    async function reverseGeocode(lat, lng) {
        try {
            if (window.google && window.google.maps) {
                const geocoder = new window.google.maps.Geocoder();
                geocoder.geocode(
                    { location: { lat: parseFloat(lat), lng: parseFloat(lng) } },
                    (results, status) => {
                        if (status === 'OK' && results[0]) {
                            address = results[0].formatted_address;
                            currentLat = parseFloat(lat);
                            currentLng = parseFloat(lng);
                        }
                    }
                );
            }
        } catch (error) {
            console.error('Reverse geocoding error:', error);
        }
    }


    function initializeAutocomplete() {
        if (!window.google || !window.google.maps || !window.google.maps.places) return;

        try {
            autocompleteService = new window.google.maps.places.AutocompleteService();

            // PlacesService richiede solo un container per l'attribuzione, non una mappa
            // vera: instanziare google.maps.Map qui genera un map load fatturato (Dynamic
            // Maps) su ogni pagina del sito, dato che questo componente vive nel footer.
            placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
        } catch (error) {
            console.error('Error initializing Google Maps services:', error);
        }
    }

    // Un solo token per tutta la digitazione: viene rigenerato solo dopo
    // che l'utente sceglie un suggerimento (o cancella la ricerca), non ad
    // ogni keystroke, altrimenti si perde lo sconto "a sessione".
    function getSessionToken() {
        if (!sessionToken) {
            sessionToken = new window.google.maps.places.AutocompleteSessionToken();
        }
        return sessionToken;
    }

    // Funzioni per gestire i suggerimenti
    function handleAddressInput(event) {
        const value = event.target.value;
        userIsTyping = true;
        userHasSelectedSuggestion = false; // Reset selection flag when user starts typing again
        address = value;
        
        if (value.length > 2) {
            searchPlaces(value);
        } else {
            hideSuggestions();
        }
        
        // Reset typing flag after a short delay
        setTimeout(() => {
            userIsTyping = false;
        }, 1000);
    }

    function searchPlaces(query) {
        if (!autocompleteService) {
            console.warn('AutocompleteService not initialized');
            return;
        }

        try {
            const request = {
                input: query,
                componentRestrictions: { country: 'IT' },
                types: ['establishment', 'geocode'],
                sessionToken: getSessionToken()
            };

            autocompleteService.getPlacePredictions(request, (predictions, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                    suggestions = predictions.slice(0, 5); // Limite a 5 suggerimenti
                    showSuggestions = true;
                    selectedSuggestionIndex = -1;
                } else {
                    console.warn('SearchPlaces failed:', status);
                    hideSuggestions();
                }
            });
        } catch (error) {
            console.error('Error in searchPlaces:', error);
            hideSuggestions();
        }
    }

    function selectSuggestion(suggestion) {
        userIsTyping = false; // User selected a suggestion, stop typing mode
        userHasSelectedSuggestion = true; // Mark that user made a manual selection
        address = suggestion.description;
        hideSuggestions();
        addressInput.blur();
        
        // Get place details directly using place_id
        if (suggestion.place_id && placesService) {
            placesService.getDetails({
                placeId: suggestion.place_id,
                fields: ['geometry', 'place_id', 'name'],
                sessionToken: getSessionToken()
            }, (place, status) => {
                // La sessione si chiude qui: la prossima ricerca ne apre una nuova.
                sessionToken = null;
                if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
                    currentLat = place.geometry.location.lat();
                    currentLng = place.geometry.location.lng();
                    currentPid = place.place_id;
                }
            });
        }
    }

    function hideSuggestions() {
        showSuggestions = false;
        suggestions = [];
        selectedSuggestionIndex = -1;
    }

    function handleKeydown(event) {
        if (!showSuggestions || suggestions.length === 0) return;

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                selectedSuggestionIndex = Math.min(selectedSuggestionIndex + 1, suggestions.length - 1);
                break;
            case 'ArrowUp':
                event.preventDefault();
                selectedSuggestionIndex = Math.max(selectedSuggestionIndex - 1, -1);
                break;
            case 'Enter':
                event.preventDefault();
                if (selectedSuggestionIndex >= 0) {
                    selectSuggestion(suggestions[selectedSuggestionIndex]);
                }
                break;
            case 'Escape':
                hideSuggestions();
                break;
        }
    }

    function formatDateTime(date, time) {
        if (!date || !time) return null;
        // Formato: YYYYMMDDHHMM (es. 202506250000)
        const dateStr = date.replace(/-/g, ''); // Rimuove i trattini dalla data
        const timeStr = time.replace(':', ''); // Rimuove i due punti dall'ora
        return `${dateStr}${timeStr}`;
    }

    function handleSearch() {
        // Blocca la ricerca se il form non è valido
        if (!isFormValid) {
            return;
        }

        const startDateTime = formatDateTime(startDate, startTime);
        const endDateTime = formatDateTime(endDate, endTime);

        // Se abbiamo già le coordinate salvate (dalla ricerca iniziale), usale
        if (currentLat !== null && currentLng !== null) {
            const searchParams = new URLSearchParams();
            
            if (startDateTime) searchParams.set('data_inizio', startDateTime);
            if (endDateTime) searchParams.set('data_fine', endDateTime);
            if (address) searchParams.set('address', address);
            
            searchParams.set('lat', currentLat.toString());
            searchParams.set('long', currentLng.toString());
            if (currentPid) {
                searchParams.set('pid', currentPid);
            }

            const finalUrl = `${redirectUrl}${redirectUrl.includes('?') ? '&' : '?'}${searchParams.toString()}`;

            dispatch('search', {
                address,
                startDate: startDateTime,
                endDate: endDateTime,
                lat: currentLat,
                lng: currentLng,
                pid: currentPid,
                url: finalUrl
            });

            // Redirect to search page
            window.location.href = finalUrl;
            return;
        }

        // Altrimenti ottieni i dettagli del posto per lat/lng se l'indirizzo è cambiato
        if (address) {
            getPlaceDetails(address, (placeDetails) => {
                if (placeDetails && placeDetails.geometry) {
                    const searchParams = new URLSearchParams();
                    
                    if (startDateTime) searchParams.set('data_inizio', startDateTime);
                    if (endDateTime) searchParams.set('data_fine', endDateTime);
                    
                    const lat = placeDetails.geometry.location.lat();
                    const lng = placeDetails.geometry.location.lng();
                    
                    searchParams.set('lat', lat.toString());
                    searchParams.set('long', lng.toString());
                    if (placeDetails.place_id) {
                        searchParams.set('pid', placeDetails.place_id);
                    }

                    const finalUrl = `${redirectUrl}${redirectUrl.includes('?') ? '&' : '?'}${searchParams.toString()}`;

                    dispatch('search', {
                        address,
                        startDate: startDateTime,
                        endDate: endDateTime,
                        placeDetails,
                        url: finalUrl
                    });

                    // Redirect to search page
                    window.location.href = finalUrl;
                } else {
                    // Nessuna coordinata trovata
                    console.warn('Impossibile ottenere le coordinate per l\'indirizzo:', address);
                    alert($t('components.search_bar.coordinates_error', 'Error retrieving coordinates. Try with another address.'));
                }
            });
        }
    }

    function getPlaceDetails(address, callback) {
        if (!autocompleteService || !placesService) {
            console.warn('Google Maps services not initialized');
            callback(null);
            return;
        }

        try {
            // Round-trip autonomo (non legato alla digitazione in corso): usa
            // un proprio token di sessione dedicato per le due chiamate.
            const fallbackSessionToken = new window.google.maps.places.AutocompleteSessionToken();

            // Cerca il posto corrispondente all'indirizzo
            const request = {
                input: address,
                componentRestrictions: { country: 'IT' },
                types: ['establishment', 'geocode'],
                sessionToken: fallbackSessionToken
            };

            autocompleteService.getPlacePredictions(request, (predictions, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions && predictions.length > 0) {
                    // Usa il primo risultato
                    const placeId = predictions[0].place_id;

                    placesService.getDetails({
                        placeId: placeId,
                        fields: ['geometry', 'place_id', 'name'],
                        sessionToken: fallbackSessionToken
                    }, (place, status) => {
                        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                            callback(place);
                        } else {
                            console.warn('PlacesService getDetails failed:', status);
                            callback(null);
                        }
                    });
                } else {
                    console.warn('AutocompleteService failed:', status);
                    callback(null);
                }
            });
        } catch (error) {
            console.error('Error in getPlaceDetails:', error);
            callback(null);
        }
    }

    function getTodayDate() {
        return new Date().toISOString().split('T')[0];
    }

    function getCurrentTime() {
        const now = new Date();
        return now.toTimeString().slice(0, 5);
    }

    // Modal functions
    function openModal(type) {
        modalType = type;
        if (type === 'start') {
            if (startDate && startTime) {
                tempDate = startDate;
                tempTime = startTime;
            } else {
                const defaultStart = getDefaultStartDateTime();
                tempDate = defaultStart.date;
                tempTime = defaultStart.time;
            }
        } else {
            tempDate = endDate || getTomorrowDate();
            tempTime = endTime || '18:00';
        }
        showModal = true;
    }

    function closeModal() {
        showModal = false;
        modalType = '';
        tempDate = '';
        tempTime = '';
    }

    function confirmSelection() {
        if (modalType === 'start') {
            startDate = tempDate;
            startTime = tempTime;
            
            // Se non esiste data di fine, impostala al giorno successivo
            if (!endDate) {
                const d = new Date(startDate);
                d.setDate(d.getDate() + 1);
                endDate = d.toISOString().split('T')[0];
                endTime = endTime || '18:00';
            } else {
                // Confronta data+ora in locale per capire se end <= start
                const toDateTime = (dateStr, timeStr) => {
                    const time = timeStr || '00:00';
                    return new Date(`${dateStr}T${time}:00`);
                };

                const startDT = toDateTime(startDate, startTime);
                const endDT = toDateTime(endDate, endTime);

                // Se end è uguale o precedente a start, sposta end al giorno successivo
                if (endDT <= startDT) {
                    const d = new Date(startDate);
                    d.setDate(d.getDate() + 2); // +2 giorni per evitare uguaglianza
                    endDate = d.toISOString().split('T')[0];
                    // Mantieni l'orario di fine se era già impostato, altrimenti usa 18:00
                    endTime = endTime || '18:00';
                }
            }
        } else {
            // Verifica che la data di fine non sia precedente alla data di inizio
            if (startDate && tempDate < startDate) {
                alert($t('components.search_bar.end_before_start_error', 'End date cannot be before start date!'));
                return;
            }
            endDate = tempDate;
            endTime = tempTime;
        }
        closeModal();
    }

    function getTomorrowDate() {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow.toISOString().split('T')[0];
    }

    // Calendar functions
    function generateCalendar(year, month, minDate = null) {
        const firstDay = new Date(year, month, 1);
        const startCalendarDate = new Date(firstDay);
        
        // Adjust start date based on language preference
        // English: Sunday = 0, Monday = 1, etc.
        // IT/FR: Monday = 0, Tuesday = 1, etc.
        let firstDayOfWeek = firstDay.getDay();
        if (lang !== 'en') {
            // For European calendars, adjust so Monday = 0
            firstDayOfWeek = (firstDayOfWeek + 6) % 7;
        }
        
        startCalendarDate.setDate(startCalendarDate.getDate() - firstDayOfWeek);
        
        const days = [];
        const current = new Date(startCalendarDate);
        
        // Ottieni la data minima come stringa per confronto
        const minDateString = minDate ? minDate.toISOString().split('T')[0] : null;
        
        for (let i = 0; i < 42; i++) {
            const isCurrentMonth = current.getMonth() === month;
            // Crea la stringa data in modo corretto per evitare problemi di fuso orario
            const dateString = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`;
            const isDisabled = minDateString && dateString < minDateString;
            
            days.push({
                date: new Date(current),
                dateString,
                day: current.getDate(),
                isCurrentMonth,
                isDisabled
            });
            
            current.setDate(current.getDate() + 1);
        }
        
        return days;
    }

    function previousMonth() {
        if (currentMonth === 0) {
            currentMonth = 11;
            currentYear--;
        } else {
            currentMonth--;
        }
    }

    function nextMonth() {
        if (currentMonth === 11) {
            currentMonth = 0;
            currentYear++;
        } else {
            currentMonth++;
        }
    }

    function selectDate(dateString) {
        tempDate = dateString;
    }

    // Genera orari ogni 30 minuti per tutte le 24 ore
    function generateTimeOptions() {
        const times = [];
        for (let hour = 0; hour < 24; hour++) {
            times.push(`${hour.toString().padStart(2, '0')}:00`);
            times.push(`${hour.toString().padStart(2, '0')}:30`);
        }
        return times;
    }

    const timeOptions = generateTimeOptions();

    // Funzione per ottenere orario minimo (ora corrente + 2 ore)
    function getMinimumTime(selectedDate) {
        const now = new Date();
        const today = now.toISOString().split('T')[0];
        
        // Se la data selezionata è oggi, calcola orario minimo
        if (selectedDate === today) {
            const minTime = new Date(now.getTime() + 2 * 60 * 60 * 1000); // +2 ore
            
            // Arrotonda alla mezzora successiva
            const minutes = minTime.getMinutes();
            if (minutes > 0 && minutes <= 30) {
                minTime.setMinutes(30);
            } else if (minutes > 30) {
                minTime.setHours(minTime.getHours() + 1);
                minTime.setMinutes(0);
            }
            
            return minTime.toTimeString().slice(0, 5);
        }
        
        // Se è un giorno futuro, nessuna restrizione
        return '00:00';
    }

    // Computed property per filtrare orari validi in base alla data selezionata
    $: validTimeOptions = modalType === 'start' && tempDate ? 
        timeOptions.filter(time => {
            const minTime = getMinimumTime(tempDate);
            return time >= minTime;
        }) : timeOptions;

    // Verifica se l'orario corrente è valido e resetta se necessario
    $: if (modalType === 'start' && tempDate && tempTime) {
        const minTime = getMinimumTime(tempDate);
        if (tempTime < minTime) {
            // Seleziona il primo orario valido disponibile
            const firstValidTime = validTimeOptions[0];
            if (firstValidTime) {
                tempTime = firstValidTime;
            }
        }
    }

    // Computed property per validare se tutti i campi sono compilati
    $: isFormValid = address && address.trim() !== '' && 
                     startDate && startDate !== '' && 
                     startTime && startTime !== '' && 
                     endDate && endDate !== '' && 
                     endTime && endTime !== '';

    // Get localized month and day names
    $: monthNames = [
        $t('calendar.months.jan'), $t('calendar.months.feb'), $t('calendar.months.mar'), 
        $t('calendar.months.apr'), $t('calendar.months.may'), $t('calendar.months.jun'),
        $t('calendar.months.jul'), $t('calendar.months.aug'), $t('calendar.months.sep'), 
        $t('calendar.months.oct'), $t('calendar.months.nov'), $t('calendar.months.dec')
    ];

    // Get localized day names with proper ordering based on language
    $: dayNames = getDayNamesForLanguage(lang);

    function getDayNamesForLanguage(language) {
        const dayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
        const localizedDays = dayKeys.map(key => $t(`calendar.days.${key}`));
        
        // Different week start based on language
        // US/English: Sunday first
        // Europe (IT/FR): Monday first
        if (language === 'en') {
            return localizedDays; // Sunday first: [Sun, Mon, Tue, Wed, Thu, Fri, Sat]
        } else {
            // Monday first: [Mon, Tue, Wed, Thu, Fri, Sat, Sun]
            return [...localizedDays.slice(1), localizedDays[0]];
        }
    }

    function formatDisplayDate(date) {
        if (!date) return $t('components.search_bar.select_date', 'Select date');
        // Evita problemi di fuso orario creando la data dai singoli componenti
        const [year, month, day] = date.split('-').map(Number);
        const d = new Date(year, month - 1, day);
        
        // Use locale based on language
        const locale = lang === 'en' ? 'en-US' : lang === 'fr' ? 'fr-FR' : 'it-IT';
        return d.toLocaleDateString(locale, { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric' 
        });
    }

    // Funzione per ottenere data e ora di default che rispettano la regola delle 2 ore
    function getDefaultStartDateTime() {
        const now = new Date();
        const timezoneOffset = now.getTimezoneOffset() * 60000;
        const localNow = new Date(now.getTime() - timezoneOffset);
        const minTime = new Date(localNow.getTime() + 2 * 60 * 60 * 1000); // +2 ore
        
        // Arrotonda alla mezzora successiva
        const minutes = minTime.getMinutes();
        if (minutes > 0 && minutes <= 30) {
            minTime.setMinutes(30);
        } else if (minutes > 30) {
            minTime.setHours(minTime.getHours() + 1);
            minTime.setMinutes(0);
        }
        
        // Se l'ora risultante è troppo tarda (dopo le 22:00), salta al giorno dopo alle 10:00
        if (minTime.getHours() >= 22) {
            const tomorrow = new Date(now);
            tomorrow.setDate(tomorrow.getDate() + 1);
            tomorrow.setHours(10, 0, 0, 0);
            return {
                date: tomorrow.toISOString().split('T')[0],
                time: '10:00'
            };
        }
        
        return {
            date: minTime.toISOString().split('T')[0],
            time: minTime.toTimeString().slice(0, 5)
        };
    }

    // Helper function to convert YYYYMMDDHHMM to date and time
    function parseCustomDate(dateStr) {
        if (!dateStr || dateStr.length !== 12) return null;
        
        try {
            const year = dateStr.substring(0, 4);
            const month = dateStr.substring(4, 6);
            const day = dateStr.substring(6, 8);
            const hour = dateStr.substring(8, 10);
            const minute = dateStr.substring(10, 12);
            
            return {
                date: `${year}-${month}-${day}`,
                time: `${hour}:${minute}`
            };
        } catch (error) {
            return null;
        }
    }

    // Reactive assignment for initial address - only update if user hasn't made a manual selection
    $: if (initialAddress && initialAddress !== address && (!browser || (document.activeElement !== addressInput && !userIsTyping && !userHasSelectedSuggestion))) {
        address = initialAddress;
    }

    // Set default values - use initial props if provided
    onMount(() => {
        if (browser) {
            // Use initial address if provided
            if (initialAddress) {
                address = initialAddress;
            }

            // Store initial coordinates if provided
            if (initialLat !== null && initialLng !== null) {
                currentLat = initialLat;
                currentLng = initialLng;
            }
            if (initialPid) {
                currentPid = initialPid;
            }
            
            // Use initial dates if provided, otherwise use defaults
            if (initialStartDate && initialStartTime) {
                const parsed = parseCustomDate(initialStartDate);
                startDate = parsed ? parsed.date : initialStartDate;
                startTime = initialStartTime || (parsed ? parsed.time : '17:30');
            } else {
                const defaultStart = getDefaultStartDateTime();
                startDate = defaultStart.date;
                startTime = defaultStart.time;
            }
            
            if (initialEndDate && initialEndTime) {
                const parsed = parseCustomDate(initialEndDate);
                endDate = parsed ? parsed.date : initialEndDate;
                endTime = initialEndTime || (parsed ? parsed.time : '18:00');
            } else {
                // Calculate end date based on start date
                const startDateObj = startDate ? new Date(startDate) : new Date(getDefaultStartDateTime().date);
                const endDateObj = new Date(startDateObj);
                endDateObj.setDate(endDateObj.getDate() + 1);
                endDate = endDateObj.toISOString().split('T')[0];
                endTime = '18:00';
            }
        }
    });
</script>

<div
    class="search-bar"
    class:unstyled
    data-searchbar-id={instanceId}
    role="search"
    aria-label={$t('components.search_bar.aria_region', 'Car search and booking')}
>
    <div class="search-container">
        <!-- Address Input -->
        <div class="search-field address-field">
            <div class="field-icon" aria-hidden="true">
                <Search size={20} />
            </div>
            <div class="address-input-container">
                <label class="sr-only" for="address-input-{instanceId}">
                    {$t('components.search_bar.location_label', 'Pick-up location')}
                </label>
                <input
                    bind:this={addressInput}
                    bind:value={address}
                    type="text"
                    placeholder={$t('routes.home.search.placeholder', 'Search for a car')}
                    class="search-input"
                    id="address-input-{instanceId}"
                    role="combobox"
                    aria-autocomplete="list"
                    aria-expanded={showSuggestions && suggestions.length > 0}
                    aria-controls="suggestions-{instanceId}"
                    aria-label={$t('components.search_bar.location_label', 'Pick-up location')}
                    aria-describedby="address-hint-{instanceId}"
                    on:input={handleAddressInput}
                    on:keydown={handleKeydown}
                    on:focus={() => address.length > 2 && searchPlaces(address)}
                />
                <span id="address-hint-{instanceId}" class="sr-only">
                    {$t('components.search_bar.location_hint', 'Type a city or address in Italy, then pick a suggestion.')}
                </span>

                {#if showSuggestions && suggestions.length > 0}
                    <ul
                        class="suggestions-dropdown"
                        id="suggestions-{instanceId}"
                        role="listbox"
                        aria-label={$t('components.search_bar.suggestions_label', 'Location suggestions')}
                    >
                        {#each suggestions as suggestion, index}
                            <li role="presentation">
                                <button
                                    type="button"
                                    class="suggestion-item"
                                    class:selected={index === selectedSuggestionIndex}
                                    role="option"
                                    aria-selected={index === selectedSuggestionIndex}
                                    on:click={() => selectSuggestion(suggestion)}
                                >
                                    <div class="suggestion-main">
                                        {suggestion.structured_formatting?.main_text || suggestion.description}
                                    </div>
                                    {#if suggestion.structured_formatting?.secondary_text}
                                        <div class="suggestion-secondary">
                                            {suggestion.structured_formatting.secondary_text}
                                        </div>
                                    {/if}
                                </button>
                            </li>
                        {/each}
                    </ul>
                {/if}

                {#if address}
                    <button
                        type="button"
                        class="clear-btn"
                        aria-label={$t('components.search_bar.clear', 'Clear address')}
                        on:click={() => {
                            address = '';
                            currentLat = null;
                            currentLng = null;
                            currentPid = null;
                            userHasSelectedSuggestion = false;
                            sessionToken = null;
                            hideSuggestions();
                            // Return focus to the input
                            addressInput && addressInput.focus();
                        }}
                    >
                        ×
                    </button>
                {/if}
            </div>
        </div>

        <!-- Start Date & Time -->
        <button
            type="button"
            class="search-field datetime-field"
            on:click={() => openModal('start')}
            aria-label="{$t('components.search_bar.start_label', 'Trip start date and time')}: {formatDisplayDate(startDate)} {startTime || ''}"
            aria-haspopup="dialog"
        >
            <div class="field-icon" aria-hidden="true">
                <Calendar size={20} />
            </div>
            <div class="datetime-display">
                <div class="date-display">{formatDisplayDate(startDate)}</div>
                <div class="date-display">{startTime || $t('components.search_bar.time', 'Time')}</div>
            </div>
        </button>

        <!-- End Date & Time -->
        <button
            type="button"
            class="search-field datetime-field"
            on:click={() => openModal('end')}
            aria-label="{$t('components.search_bar.end_label', 'Trip end date and time')}: {formatDisplayDate(endDate)} {endTime || ''}"
            aria-haspopup="dialog"
        >
            <div class="field-icon" aria-hidden="true">
                <Calendar size={20} />
            </div>
            <div class="datetime-display">
                <div class="date-display">{formatDisplayDate(endDate)}</div>
                <div class="date-display">{endTime || $t('components.search_bar.time', 'Time')}</div>
            </div>
        </button>

        <!-- Search Button -->
        <div class="search-button-container">
            <Button
                icon={Search}
                label={$t('components.search_bar.cta', 'Search')}
                backgroundColor="var(--orange)"
                textColor="white"
                onclick={handleSearch}
                size="full-width"
                disabled={!isFormValid}
            />
        </div>
    </div>
</div>

<!-- DateTime Modal -->
{#if showModal}
    <div class="modal-overlay" on:click={closeModal}>
        <div class="modal-content" on:click|stopPropagation>
            <div class="modal-header">
                <h3 class="modal-title">
                    {modalType === 'start' ? 
                        $t('components.search_bar.select_start_datetime', 'Select start date and time') : 
                        $t('components.search_bar.select_end_datetime', 'Select end date and time')
                    }
                </h3>
                <button class="close-btn" on:click={closeModal}>×</button>
            </div>
            
            <div class="modal-body">
                <!-- Calendar -->
                <div class="calendar-section">
                    <div class="calendar-header">
                        <button class="nav-btn" on:click={previousMonth}>‹</button>
                        <h4>{monthNames[currentMonth]} {currentYear}</h4>
                        <button class="nav-btn" on:click={nextMonth}>›</button>
                    </div>
                    
                    <div class="calendar-weekdays">
                        {#each dayNames as day}
                            <div class="weekday">{day}</div>
                        {/each}
                    </div>
                    
                    <div class="calendar-days">
                        {#each generateCalendar(currentYear, currentMonth, modalType === 'end' && startDate ? new Date(startDate) : new Date()) as day}
                            <button 
                                class="day-btn"
                                class:other-month={!day.isCurrentMonth}
                                class:disabled={day.isDisabled}
                                class:selected={day.dateString === tempDate}
                                disabled={day.isDisabled}
                                on:click={() => selectDate(day.dateString)}
                            >
                                {day.day}
                            </button>
                        {/each}
                    </div>
                </div>
                
                <!-- Time Selection -->
                <div class="time-section">
                    <label for="time-select-{instanceId}">{$t('components.search_bar.time_label', 'Time')}:</label>
                    <select
                        id="time-select-{instanceId}"
                        bind:value={tempTime}
                        class="time-select"
                    >
                        {#each validTimeOptions as time}
                            <option value={time}>{time}</option>
                        {/each}
                    </select>
                    
                    {#if modalType === 'start' && tempDate === getTodayDate()}
                        <div class="time-warning">
                            ⏰ {$t('components.search_bar.booking_warning', 'Bookings must start at least 2 hours after current time')}
                        </div>
                    {/if}
                </div>
            </div>
            
            <div class="modal-footer">
                <button class="btn-cancel" on:click={closeModal}>
                    {$t('components.search_bar.cancel', 'Cancel')}
                </button>
                <button class="btn-confirm" on:click={confirmSelection}>
                    {$t('components.search_bar.confirm', 'Confirm')}
                </button>
            </div>
        </div>
    </div>
{/if}


<style>
    .search-bar {
        width: 100%;
        max-width: 1400px;
        margin: 0 auto;
        padding: 0 1rem;
    }

    .search-container {
        display: flex;
        align-items: center;
        background: white;
        border-radius: 16px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        overflow: visible;
        border: 1px solid #e5e5e5;
        position: relative;
        gap: 0px;
        padding: 8px;
    }

    /* Unstyled version - remove box styling */
    .search-bar.unstyled {
        padding: 0;
    }

    .search-bar.unstyled .search-container {
        background: transparent;
        border-radius: 0;
        box-shadow: none;
        border: none;
        padding: 0;
    }

    .search-field {
        position: relative;
        display: flex;
        align-items: center;
        flex: 1;
        min-height: 32px;
        padding: 8px;
        margin: 8px;
        border-radius: 8px;
        height: 48px;
    }

    .search-field:last-child {
        border-right: none;
    }

    .address-field {
        flex: 3;
        position: relative;
    }

    .address-input-container {
        position: relative;
        width: 100%;
    }

    /* Visually hidden but available to screen readers and headless agents */
    .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
    }

    .datetime-field {
        flex: 1;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: background-color 0.2s ease;
        /* Reset native <button> styling so it matches the previous <div> layout */
        appearance: none;
        background: none;
        border: none;
        font: inherit;
        color: inherit;
        text-align: left;
        width: auto;
    }

    .datetime-field:hover {
        background-color: #f8f9fa;
    }

    .field-icon {
        color: var(--won-surface);
        margin-right: 0.65rem;
        flex-shrink: 0;
    }

    .search-input {
        width: 100%;
        border: none;
        outline: none;
        font-size: 1rem;
        font-weight: 500;
        color: var(--on-surface);
        background: transparent;
        
    }

    .search-input::placeholder {
        color: #999;
        font-weight: 400;
    }

    .datetime-display {
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        gap: 0.25rem;
        width: 100%;
    }

    .date-display {
        font-size: 0.9rem;
        font-weight: 500;
        color: var(--on-surface);
    }

    .time-display {
        font-size: 0.8rem;
        color: #666;
        font-weight: 400;
    }

    .field-label {
        font-size: 0.75rem;
        font-weight: 600;
        color: #666;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 0.25rem;
    }

    .search-button-container {
        display: flex;
        align-items: center;
        padding: 1rem;
        flex-shrink: 0;
        padding: 0;
        margin-right: 8px;
    }

    /* Mobile Responsiveness */
    @media (max-width: 768px) {
        .search-container {
            flex-direction: column;
            align-items: stretch;
            border-radius: 12px;
            gap: 0;
            
            width: 100%;
        }

        .search-field {
            border-right: none;
            min-height: 60px;
            padding: 0.75rem 1rem;
            background-color: #f9f9f9;
        }

        .search-field:last-child {
            border-bottom: none;
        }

        .datetime-field {
            flex-direction: row;
            align-items: center;
        }

        .field-label {
            margin-bottom: 0;
            margin-right: 0.75rem;
            min-width: 70px;
        }

        .datetime-inputs {
            margin-top: 0;
        }

        .search-button-container {
            padding: 0;
            padding-top: 16px;
        }
    }

    @media (max-width: 480px) {
        .search-bar {
            padding: 0 0.5rem;
        }

        .search-field {
            padding: 0.5rem;
        }

        .datetime-inputs {
            flex-direction: column;
            gap: 0.25rem;
            align-items: stretch;
        }

        .time-input {
            width: 100%;
        }
    }

    /* Focus states */
    .search-input:focus,
    .date-input:focus,
    .time-input:focus {
        outline-offset: -2px;
        border-radius: 4px;
    }

    /* Hover effects */
    .search-field:hover {
        background-color: #f1f1f1;
    }

    .search-field:hover .field-icon {
        color: var(--orange);
        transform: scale(1.05);
        transition: all 0.2s ease;
    }

    /* Suggestions Dropdown */
    .suggestions-dropdown {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: white;
        border: 1px solid #e5e5e5;
        border-radius: 12px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
        z-index: 1000;
        margin-top: 0.5rem;
        max-height: 300px;
        overflow-y: auto;
        animation: fadeInScale 0.2s ease-out;
        /* Reset <ul> defaults now that the dropdown is a semantic list */
        list-style: none;
        padding: 0;
    }

    .suggestions-dropdown li {
        margin: 0;
        padding: 0;
    }

    .clear-btn {
        position: absolute; 
        right: 0.5rem; 
        top: 50%; 
        transform: translateY(-50%); 
        background: none; 
        border: none; 
        font-size: 2rem; 
        cursor: pointer; 
        padding: 0.25rem; 
        color: #666;
    }

    @keyframes fadeInScale {
        from {
            opacity: 0;
            transform: translateY(-10px) scale(0.95);
        }
        to {
            opacity: 1;
            transform: translateY(0) scale(1);
        }
    }

    .suggestion-item {
        display: block;
        width: 100%;
        padding: 1rem;
        border: none;
        background: none;
        text-align: left;
        cursor: pointer;
        border-bottom: 1px solid #f5f5f5;
        transition: background-color 0.2s ease;
    }

    .suggestions-dropdown li:last-child .suggestion-item {
        border-bottom: none;
    }

    .suggestion-item:hover,
    .suggestion-item.selected {
        background-color: #f8f9fa;
    }

    .suggestion-item.selected {
        background-color: #e3f2fd;
    }

    .suggestion-main {
        font-size: 0.9rem;
        font-weight: 500;
        color: var(--on-surface);
        margin-bottom: 0.25rem;
    }

    .suggestion-secondary {
        font-size: 0.8rem;
        color: #666;
        line-height: 1.3;
    }

    /* Modal Styles */
    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.6);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        padding: 1rem;
        animation: fadeIn 0.3s ease-out;
    }

    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }

    .modal-content {
        background: white;
        border-radius: 20px;
        max-width: 450px;
        width: 100%;
        max-height: 90vh;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        box-shadow: 0 25px 50px rgba(0, 0, 0, 0.2);
        animation: slideUp 0.3s ease-out;
    }

    @keyframes slideUp {
        from {
            opacity: 0;
            transform: translateY(30px) scale(0.95);
        }
        to {
            opacity: 1;
            transform: translateY(0) scale(1);
        }
    }

    .modal-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 1.5rem 1.5rem 1rem 1.5rem;
        border-bottom: 1px solid #f0f0f0;
        flex-shrink: 0;
    }

    .modal-title {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 600;
        color: var(--on-surface);
    }

    .close-btn {
        background: none;
        border: none;
        font-size: 1.5rem;
        color: #999;
        cursor: pointer;
        padding: 0.25rem;
        border-radius: 50%;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
    }

    .close-btn:hover {
        background: #f5f5f5;
        color: #666;
    }

    .modal-body {
        padding: 1.5rem;
        overflow-y: auto;
        flex: 1;
    }

    .calendar-section {
        margin-bottom: 1.5rem;
    }

    .calendar-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 1rem;
        padding: 0 0.5rem;
    }

    .calendar-header h4 {
        margin: 0;
        font-size: 1.1rem;
        font-weight: 600;
        color: var(--on-surface);
    }

    .nav-btn {
        background: none;
        border: none;
        font-size: 1.5rem;
        color: #666;
        cursor: pointer;
        padding: 0.5rem;
        border-radius: 8px;
        transition: all 0.2s ease;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .nav-btn:hover {
        background: #f5f5f5;
        color: var(--orange);
    }

    .calendar-weekdays {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        gap: 2px;
        margin-bottom: 0.5rem;
    }

    .weekday {
        text-align: center;
        font-size: 0.75rem;
        font-weight: 600;
        color: #666;
        padding: 0.75rem 0.5rem;
    }

    .calendar-days {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        gap: 2px;
    }

    .day-btn {
        background: none;
        border: none;
        padding: 0.75rem 0.5rem;
        font-size: 0.875rem;
        cursor: pointer;
        border-radius: 8px;
        transition: all 0.2s ease;
        color: var(--on-surface);
        font-weight: 500;
        position: relative;
    }

    .day-btn:hover:not(.disabled) {
        background: #e3f2fd;
        color: var(--orange);
    }

    .day-btn.selected {
        background: var(--orange);
        color: white;
        font-weight: 600;
    }

    .day-btn.other-month {
        color: #ccc;
    }

    .day-btn.disabled {
        color: #ddd;
        cursor: not-allowed;
    }

    .time-section {
        padding: 1rem;
        background: #f8f9fa;
        border-radius: 12px;
        display: flex;
        align-items: center;
        gap: 1rem;
    }

    .time-section label {
        font-size: 1rem;
        font-weight: 500;
        color: var(--on-surface);
        min-width: 60px;
    }

    .time-select {
        flex: 1;
        padding: 0.75rem;
        border: 2px solid #e5e5e5;
        border-radius: 8px;
        font-size: 1rem;
        font-weight: 500;
        color: var(--on-surface);
        transition: border-color 0.2s ease;
        cursor: pointer;
        background: white;
        appearance: none;
        background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
        background-repeat: no-repeat;
        background-position: right 0.75rem center;
        background-size: 1rem;
        padding-right: 2.5rem;
    }

    .time-select:focus {
        outline: none;
        border-color: var(--orange);
        box-shadow: 0 0 0 3px rgba(var(--orange-rgb, 0, 150, 136), 0.1);
    }

    .time-select:hover {
        border-color: #d1d5d9;
    }

    .time-warning {
        margin-top: 0.5rem;
        padding: 0.5rem;
        background: #fff3cd;
        border: 1px solid #ffeaa7;
        border-radius: 6px;
        font-size: 0.875rem;
        color: #856404;
        text-align: center;
    }

    .modal-footer {
        display: flex;
        gap: 1rem;
        justify-content: flex-end;
        padding: 1rem 1.5rem 1.5rem 1.5rem;
        border-top: 1px solid #f0f0f0;
        flex-shrink: 0;
    }

    .btn-cancel,
    .btn-confirm {
        padding: 0.75rem 1.5rem;
        border: none;
        border-radius: 10px;
        font-size: 0.875rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        min-width: 100px;
    }

    .btn-cancel {
        background: #f5f5f5;
        color: #666;
    }

    .btn-cancel:hover {
        background: #e5e5e5;
        color: #333;
    }

    .btn-confirm {
        background: var(--orange);
        color: white;
    }

    .btn-confirm:hover {
        background: var(--orange-dark, #00695c);
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(var(--orange-rgb, 0, 150, 136), 0.3);
    }

    /* Mobile Modal Styles */
    @media (max-width: 480px) {
        .modal-content {
            margin: 0.5rem;
            max-width: none;
            border-radius: 16px;
        }

        .modal-header,
        .modal-body,
        .modal-footer {
            padding: 1rem;
        }

        .modal-title {
            font-size: 1.1rem;
        }

        .calendar-days {
            gap: 1px;
        }

        .day-btn {
            padding: 0.5rem 0.25rem;
            font-size: 0.8rem;
        }

        .weekday {
            padding: 0.5rem 0.25rem;
            font-size: 0.7rem;
        }
    }

</style>